import { Utensils, CalendarOff, BedDouble } from 'lucide-react';
import { dateKey, MONTHS_FR } from '../../utils/dateUtils';
import type { GridRow, DailyIndemnity } from './types';

// --- COMPOSANTS UI INTERNES ---
const MealToggle = ({ checked, onChange, disabled }: { checked: boolean, onChange: (val: boolean) => void, disabled: boolean }) => (
  <button
    disabled={disabled}
    onClick={() => onChange(!checked)}
    style={{
      width: '100%', padding: '14px', border: `2px solid ${checked ? '#10b981' : '#ccc'}`, background: checked ? '#dcfce7' : 'white',
      color: checked ? '#10b981' : '#ccc', borderRadius: '10px', cursor: disabled ? 'not-allowed' : 'pointer', fontWeight: 'bold', fontSize: '14px', opacity: disabled ? 0.6 : 1
    }}
  >
    {checked ? 'OUI' : 'NON'}
  </button>
);

const BinaryToggle = ({ value, onChange, disabled, label, simpleDisplay }: { value: number, onChange: (val: number) => void, disabled: boolean, label: string, simpleDisplay?: boolean }) => {
  const isChecked = value === 1;
  // simpleDisplay: affiche "OUI"/"NON" sans le carré checkbox
  const displayText = simpleDisplay
    ? (isChecked ? 'OUI' : 'NON')
    : (isChecked ? `1 ${label}` : label);
  return (
    <button
      disabled={disabled}
      onClick={() => onChange(isChecked ? 0 : 1)}
      style={{
        width: '100%', padding: '14px',
        border: `2px solid ${isChecked ? '#f59e0b' : '#ccc'}`,
        background: isChecked ? '#fffbeb' : 'white',
        color: isChecked ? '#b45309' : '#ccc',
        borderRadius: '10px', cursor: disabled ? 'not-allowed' : 'pointer',
        fontWeight: 'bold', fontSize: '14px', opacity: disabled ? 0.6 : 1,
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
      }}
    >
      {!simpleDisplay && (
        <div style={{
          width: '16px', height: '16px', borderRadius: '4px', border: '2px solid currentColor',
          background: isChecked ? 'currentColor' : 'transparent', display:'inline-block'
        }}></div>
      )}
      {displayText}
    </button>
  );
};

interface IndemnitySectionProps {
  indemnities: Record<string, DailyIndemnity>;
  weekDays: Date[];
  holidays: string[];
  isOutsideClosureMonth: (day: Date) => boolean;
  isLockedForDay: (day: Date) => boolean;
  isDayBlocked: (day: Date) => boolean;
  isTraining: (day: Date) => boolean;
  onIndemnityChange: (dateStr: string, field: keyof DailyIndemnity, value: string | number | boolean) => void;
  getGDWorkDaysInfo: (rows: GridRow[]) => { gdWorkDays: string[]; lastGDDay: string | null };
  rows: GridRow[];
}

export function IndemnitySection({
  indemnities,
  weekDays,
  holidays,
  isOutsideClosureMonth,
  isLockedForDay,
  isDayBlocked,
  isTraining,
  onIndemnityChange,
  getGDWorkDaysInfo,
  rows,
}: IndemnitySectionProps) {
  return (
    <div style={{ background: '#f0fdf4', borderRadius: '20px', border: '1px solid #bbf7d0', overflow: 'hidden', marginBottom:'50px', boxShadow:'0 2px 4px rgba(0,0,0,0.05)' }}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', minWidth: '900px', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '220px' }} />
            {weekDays.map((_, i) => <col key={`col-ind-${i}`} style={{ width: '97px' }} />)}
          </colgroup>
          <thead>
            <tr style={{ background: '#dcfce7', borderBottom: '1px solid #bbf7d0' }}>
              <th style={{ padding: '16px 8px', textAlign: 'left', color: '#166534', fontSize:'0.9rem' }}>
                <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                  <Utensils size={20} /> INDEMNITÉS & TRAJETS
                </div>
              </th>
              {weekDays.map(day => {
                const dStr = dateKey(day);
                const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                const isOutsideMonth = isOutsideClosureMonth(day);
                return (
                  <th key={`ind-header-${dStr}`} style={{
                    padding: '16px 8px',
                    textAlign: 'center',
                    background: isOutsideMonth ? '#e2e8f0' : (isWeekend ? '#fffdf2' : 'transparent'),
                    color: isOutsideMonth ? '#94a3b8' : (isWeekend ? '#b45309' : '#15803d'),
                    opacity: isOutsideMonth ? 0.7 : 1
                  }}>
                    <div style={{ fontSize: '0.85rem', textTransform: 'uppercase', fontWeight: '600' }}>{day.toLocaleDateString('fr-FR', { weekday: 'short' })}</div>
                    <div style={{ fontSize: '1.4rem', fontWeight: '800', marginTop: '4px' }}>{day.getDate()}</div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            <tr style={{ borderBottom: '1px solid #d1fae5' }}>
              <td style={{ fontWeight: '600', color: '#15803d', padding: '16px', fontSize:'1.1rem' }}>Panier Repas</td>
              {(() => {
                // Calculer les infos GD une seule fois pour toute la ligne
                const { gdWorkDays, lastGDDay } = getGDWorkDaysInfo(rows);
                const hasMultipleGDDays = gdWorkDays.length > 1;

                return weekDays.map(day => {
                  const dKey = dateKey(day);
                  const isBlocked = isDayBlocked(day);
                  const isHoliday = holidays.includes(dKey);
                  const isForm = isTraining(day);
                  const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                  const isOutsideMonth = isOutsideClosureMonth(day);
                  const isGD = indemnities[dKey]?.is_grand_deplacement === true;
                  // NOUVEAU : Verrouillage jour par jour
                  const isDayLocked = isLockedForDay(day);
                  // Détecter si c'est le dernier jour GD (panier suggéré, mais modifiable)
                  const isLastGDDay = hasMultipleGDDays && dKey === lastGDDay;

                  // Jour hors mois de saisie = cellule grisée cohérente avec les heures
                  if (isOutsideMonth) return (
                    <td key={'meal-'+dKey} style={{ padding: '10px', background:'#f1f5f9', borderLeft: '1px solid #f0fdf4' }}>
                      <div style={{ display:'flex', flexDirection:'column', justifyContent:'center', alignItems:'center', color:'#94a3b8', height:'54px' }} title={`Ce jour sera à saisir sur la feuille de ${MONTHS_FR[day.getMonth()]}`}>
                        <CalendarOff size={16} style={{ opacity: 0.5 }} />
                        <span style={{ fontSize:'0.65rem', marginTop:'2px' }}>{MONTHS_FR[day.getMonth()].substring(0, 3)}.</span>
                      </div>
                    </td>
                  );
                  // Formation = Pas de panier
                  if (isForm) return <td key={'meal-'+dKey} style={{ padding: '10px', textAlign:'center', color:'#94a3b8', fontSize:'1rem', borderLeft: '1px solid #f0fdf4' }}>-</td>;

                  // Griser si jour férié
                  if (isHoliday) return (
                    <td key={'meal-'+dKey} style={{ padding: '10px', background: 'rgba(0,0,0,0.06)', borderLeft: '1px solid #f0fdf4' }}>
                      <div style={{ display:'flex', justifyContent:'center', alignItems:'center', color:'#9ca3af', height:'54px' }} title="Jour férié">
                        <CalendarOff size={20} />
                      </div>
                    </td>
                  );

                  // Dernier jour GD : panier modifiable avec style doré indicatif
                  if (isLastGDDay) {
                    return (
                      <td key={'meal-'+dKey} style={{ padding: '10px', background: '#fffbeb', borderLeft: '1px solid #f0fdf4' }}>
                        <MealToggle disabled={isDayLocked || isBlocked} checked={indemnities[dKey]?.meals === 1} onChange={(val) => onIndemnityChange(dKey, 'meals', val ? '1' : '0')} />
                      </td>
                    );
                  }

                  return (
                    <td key={'meal-'+dKey} style={{ padding: '10px', background: isWeekend ? '#fffdf2' : (isBlocked ? 'rgba(0,0,0,0.06)' : 'transparent'), borderLeft: '1px solid #f0fdf4' }}>
                      {/* Si GD activé, afficher une indication visuelle que le repas est exclu */}
                      {isGD ? (
                        <button
                          disabled
                          title="Incompatible avec Grand Déplacement"
                          style={{
                            width: '100%',
                            padding: '14px',
                            border: '2px solid #e5e7eb',
                            background: '#f3f4f6',
                            color: '#9ca3af',
                            borderRadius: '10px',
                            cursor: 'not-allowed',
                            fontWeight: 'bold',
                            fontSize: '14px',
                            opacity: 0.6
                          }}
                        >
                          NON
                        </button>
                      ) : (
                        <MealToggle disabled={isDayLocked || isBlocked} checked={indemnities[dKey]?.meals === 1} onChange={(val) => onIndemnityChange(dKey, 'meals', val ? '1' : '0')} />
                      )}
                    </td>
                  );
                });
              })()}
            </tr>

            <tr style={{ borderBottom: '1px solid #d1fae5' }}>
              <td style={{ fontWeight: '600', color: '#15803d', padding: '16px', fontSize:'1.1rem' }}>Transport</td>
              {weekDays.map(day => {
                const dKey = dateKey(day);
                const isBlocked = isDayBlocked(day);
                const isHoliday = holidays.includes(dKey);
                const isForm = isTraining(day);
                const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                const isOutsideMonth = isOutsideClosureMonth(day);
                // NOUVEAU : Verrouillage jour par jour
                const isDayLocked = isLockedForDay(day);

                // Jour hors mois de saisie = cellule grisée cohérente avec les heures
                if (isOutsideMonth) return (
                  <td key={'tr-'+dKey} style={{ padding: '10px', background:'#f1f5f9', borderLeft: '1px solid #f0fdf4' }}>
                    <div style={{ display:'flex', flexDirection:'column', justifyContent:'center', alignItems:'center', color:'#94a3b8', height:'54px' }} title={`Ce jour sera à saisir sur la feuille de ${MONTHS_FR[day.getMonth()]}`}>
                      <CalendarOff size={16} style={{ opacity: 0.5 }} />
                      <span style={{ fontSize:'0.65rem', marginTop:'2px' }}>{MONTHS_FR[day.getMonth()].substring(0, 3)}.</span>
                    </div>
                  </td>
                );
                if (isForm) return <td key={'tr-'+dKey} style={{ padding: '10px', textAlign:'center', color:'#94a3b8', fontSize:'1rem', borderLeft: '1px solid #f0fdf4' }}>-</td>;

                // Griser si jour férié
                if (isHoliday) return (
                  <td key={'transport-'+dKey} style={{ padding: '10px', background: 'rgba(0,0,0,0.06)', borderLeft: '1px solid #f0fdf4' }}>
                    <div style={{ display:'flex', justifyContent:'center', alignItems:'center', color:'#9ca3af', height:'54px' }} title="Jour férié">
                      <CalendarOff size={20} />
                    </div>
                  </td>
                );

                const isGD = indemnities[dKey]?.is_grand_deplacement === true;
                return (
                  <td key={'transport-'+dKey} style={{ padding: '10px', background: isWeekend ? '#fffdf2' : (isBlocked || isGD ? 'rgba(0,0,0,0.06)' : 'transparent'), borderLeft: '1px solid #f0fdf4' }}>
                    <BinaryToggle
                      label="Transport"
                      simpleDisplay={true}
                      disabled={isDayLocked || isBlocked || isGD}
                      value={isGD ? 0 : (indemnities[dKey]?.transport || 0)}
                      onChange={(val) => onIndemnityChange(dKey, 'transport', val)}
                    />
                  </td>
                );
              })}
            </tr>

            <tr style={{ borderBottom: '1px solid #d1fae5' }}>
              <td style={{ fontWeight: '600', color: '#15803d', padding: '16px', fontSize:'1.1rem' }}>Trajet</td>
              {weekDays.map(day => {
                const dKey = dateKey(day);
                const isBlocked = isDayBlocked(day);
                const isHoliday = holidays.includes(dKey);
                const isForm = isTraining(day);
                const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                const isOutsideMonth = isOutsideClosureMonth(day);
                // NOUVEAU : Verrouillage jour par jour
                const isDayLocked = isLockedForDay(day);

                // Jour hors mois de saisie = cellule grisée cohérente avec les heures
                if (isOutsideMonth) return (
                  <td key={'traj-'+dKey} style={{ padding: '10px', background:'#f1f5f9', borderLeft: '1px solid #f0fdf4' }}>
                    <div style={{ display:'flex', flexDirection:'column', justifyContent:'center', alignItems:'center', color:'#94a3b8', height:'54px' }} title={`Ce jour sera à saisir sur la feuille de ${MONTHS_FR[day.getMonth()]}`}>
                      <CalendarOff size={16} style={{ opacity: 0.5 }} />
                      <span style={{ fontSize:'0.65rem', marginTop:'2px' }}>{MONTHS_FR[day.getMonth()].substring(0, 3)}.</span>
                    </div>
                  </td>
                );
                if (isForm) return <td key={'traj-'+dKey} style={{ padding: '10px', textAlign:'center', color:'#94a3b8', fontSize:'1rem', borderLeft: '1px solid #f0fdf4' }}>-</td>;

                const isGD = indemnities[dKey]?.is_grand_deplacement === true;

                // Griser si jour férié
                if (isHoliday) return (
                  <td key={'trajet-'+dKey} style={{ padding: '10px', background: 'rgba(0,0,0,0.06)', borderLeft: '1px solid #f0fdf4' }}>
                    <div style={{ display:'flex', justifyContent:'center', alignItems:'center', color:'#9ca3af', height:'54px' }} title="Jour férié">
                      <CalendarOff size={20} />
                    </div>
                  </td>
                );

                return (
                  <td key={'trajet-'+dKey} style={{ padding: '10px', background: isWeekend ? '#fffdf2' : (isBlocked || isGD ? 'rgba(0,0,0,0.06)' : 'transparent'), borderLeft: '1px solid #f0fdf4' }}>
                    <BinaryToggle
                      label="Trajet"
                      simpleDisplay={true}
                      disabled={isDayLocked || isBlocked || isGD}
                      value={isGD ? 0 : (indemnities[dKey]?.trajet || 0)}
                      onChange={(val) => onIndemnityChange(dKey, 'trajet', val)}
                    />
                  </td>
                );
              })}
            </tr>

            {/* LIGNE GRAND DÉPLACEMENT */}
            <tr style={{ background: 'linear-gradient(135deg, #fef2f2, #fff)', height: '90px' }}>
              <td style={{ fontWeight: '600', color: '#dc2626', padding: '16px', fontSize:'1.1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <BedDouble size={20} /> Grand Déplacement
              </td>
              {(() => {
                // Calculer les infos GD une seule fois pour toute la ligne
                const { gdWorkDays, lastGDDay } = getGDWorkDaysInfo(rows);
                const hasMultipleGDDays = gdWorkDays.length > 1;

                return weekDays.map(day => {
                  const dKey = dateKey(day);
                  const isBlocked = isDayBlocked(day);
                  const isHoliday = holidays.includes(dKey);
                  const isForm = isTraining(day);
                  const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                  const isOutsideMonth = isOutsideClosureMonth(day);
                  const isGD = indemnities[dKey]?.is_grand_deplacement === true;
                  // NOUVEAU : Verrouillage jour par jour
                  const isDayLocked = isLockedForDay(day);
                  // NOUVEAU : Détecter si ce jour est le "dernier jour GD" (panier auto)
                  const isLastGDDay = hasMultipleGDDays && dKey === lastGDDay;

                  // Jour hors mois de saisie = cellule grisée
                  if (isOutsideMonth) return (
                    <td key={'gd-'+dKey} style={{ padding: '10px', background:'#f1f5f9', borderLeft: '1px solid #f0fdf4' }}>
                      <div style={{ display:'flex', flexDirection:'column', justifyContent:'center', alignItems:'center', color:'#94a3b8', height:'54px' }} title={`Ce jour sera à saisir sur la feuille de ${MONTHS_FR[day.getMonth()]}`}>
                        <CalendarOff size={16} style={{ opacity: 0.5 }} />
                        <span style={{ fontSize:'0.65rem', marginTop:'2px' }}>{MONTHS_FR[day.getMonth()].substring(0, 3)}.</span>
                      </div>
                    </td>
                  );

                  // Formation = pas de GD
                  if (isForm) return <td key={'gd-'+dKey} style={{ padding: '10px', textAlign:'center', color:'#94a3b8', fontSize:'1rem', borderLeft: '1px solid #f0fdf4' }}>-</td>;

                  // Griser si jour férié
                  if (isHoliday) return (
                    <td key={'gd-'+dKey} style={{ padding: '10px', background: 'rgba(0,0,0,0.06)', borderLeft: '1px solid #f0fdf4' }}>
                      <div style={{ display:'flex', justifyContent:'center', alignItems:'center', color:'#9ca3af', height:'54px' }} title="Jour férié">
                        <CalendarOff size={20} />
                      </div>
                    </td>
                  );

                  return (
                    <td key={'gd-'+dKey} style={{ padding: '10px', background: isWeekend ? '#fffdf2' : (isBlocked ? 'rgba(0,0,0,0.06)' : 'transparent'), borderLeft: '1px solid #f0fdf4', verticalAlign: 'middle', position: 'relative' }}>
                      <button
                        disabled={isDayLocked || isBlocked}
                        onClick={() => onIndemnityChange(dKey, 'is_grand_deplacement', !isGD)}
                        title={isLastGDDay ? 'Dernier jour GD : panier repas suggéré' : (isGD ? 'Désactiver Grand Déplacement' : 'Activer Grand Déplacement')}
                        style={{
                          width: '100%',
                          padding: '14px',
                          border: `2px solid ${isGD ? '#dc2626' : (isLastGDDay ? '#f59e0b' : '#ccc')}`,
                          background: isGD ? 'linear-gradient(135deg, #fef2f2, #fee2e2)' : (isLastGDDay ? '#fffbeb' : 'white'),
                          color: isGD ? '#dc2626' : (isLastGDDay ? '#b45309' : '#ccc'),
                          borderRadius: '10px',
                          cursor: (isDayLocked || isBlocked) ? 'not-allowed' : 'pointer',
                          fontWeight: 'bold',
                          fontSize: '14px',
                          opacity: (isDayLocked || isBlocked) ? 0.6 : 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px'
                        }}
                      >
                        {isGD ? 'OUI' : 'NON'}
                      </button>
                      {isLastGDDay && (
                        <div style={{ position: 'absolute', bottom: '4px', left: 0, right: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '2px', fontSize: '0.55rem', color: '#b45309', pointerEvents: 'none' }}>
                          <Utensils size={8} />
                          <span>Panier suggéré</span>
                        </div>
                      )}
                    </td>
                  );
                });
              })()}
            </tr>

          </tbody>
        </table>
      </div>
    </div>
  );
}
